import React, { ReactNode } from 'react'
import clsx from 'clsx'
import Collapse from '@material-ui/core/Collapse'
import Container, { ContainerProps } from '@material-ui/core/Container'
import CaretDownIcon from '@src/icons/CaretDownIcon'
import { makeStyles, Theme } from '@material-ui/core/styles'

interface SectionAccordionProps {
  classes?: {
    root?: string
    body?: string
  }
  children: ReactNode
  isAccordionEnabled: boolean
  renderHeading(): ReactNode
  toggleAccordion(): void
  isAccordionOpen: boolean
  headerContainerProps?: Partial<ContainerProps>
  headerPrefix?: ReactNode
  testId?: string
  additionalHeadClass?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  head: {
    display: 'flex',
    border: 'none',
    background: 'none',
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(8, 0),
    textAlign: 'left',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8, 0, 5),
    },
  },
  headIsEnabled: {
    '&:hover': {
      cursor: 'pointer',
    },
    [theme.breakpoints.up('md')]: {
      alignItems: 'baseline',
      padding: theme.spacing(8, 0),
    },
  },
  body: {
    paddingBottom: theme.spacing(8),
  },
  icon: {
    fontSize: '12px',
    transition: 'transform 0.25s',
    fill: theme.palette.primary.main,
  },
  iconIsOpen: {
    transform: 'rotate(180deg)',
  },
}))

const SectionAccordion: React.FC<SectionAccordionProps> = (props) => {
  const {
    classes: propsClasses,
    isAccordionOpen,
    toggleAccordion,
    renderHeading,
    isAccordionEnabled = true,
    children,
    testId = '',
    headerContainerProps = {},
    headerPrefix,
    additionalHeadClass,
  } = props

  const classes = useStyles()
  const iconClasses = clsx(classes.icon, {
    [classes.iconIsOpen]: isAccordionOpen,
  })
  const headClasses = clsx(classes.head, additionalHeadClass, {
    [classes.headIsEnabled]: isAccordionEnabled,
  })
  const isOpen = isAccordionOpen || !isAccordionEnabled
  const rootProps = {}

  if (testId.length) {
    rootProps['data-testid'] = testId
  }

  return (
    <div {...rootProps} className={propsClasses?.root}>
      <Container maxWidth="lg" {...headerContainerProps}>
        {headerPrefix}
        <button
          id={testId}
          type="button"
          className={headClasses}
          disabled={!isAccordionEnabled}
          onClick={toggleAccordion}
          aria-expanded={isOpen}
        >
          {renderHeading()}
          {isAccordionEnabled && <CaretDownIcon className={iconClasses} />}
        </button>
      </Container>
      <Collapse in={isOpen}>
        <div className={clsx(classes.body, propsClasses?.body)}>{children}</div>
      </Collapse>
    </div>
  )
}

export default SectionAccordion
